import { useState } from "react";
import Reveal from "../components/Reveal";
import { IconCheck, IconArrow, IconMail, IconMapPin, IconInstagram } from "../components/icons";
import heroContact from "../assets/images/hero/hero-contact.png";

const ROW = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))",
  gap: "16px",
};

const SR_ONLY = {
  position: "absolute", width: "1px", height: "1px",
  padding: 0, margin: "-1px", overflow: "hidden",
  clip: "rect(0,0,0,0)", whiteSpace: "nowrap", borderWidth: 0,
};

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  message: "",
};

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    const encode = (data) =>
      Object.keys(data)
        .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
        .join("&");
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });
      if (!response.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      {/* Hero */}
      <section className="sb-noise" style={{
        position: "relative", overflow: "hidden",
        backgroundImage: `url(${heroContact})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "clamp(60px, 10vw, 100px) 0 clamp(48px, 8vw, 80px)", textAlign: "center",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(160deg, rgba(58,24,68,0.75), rgba(80,30,60,0.68))",
          zIndex: 0,
        }} />
        <div className="sb-container" style={{ position: "relative", zIndex: 2 }}>
          <Reveal><span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold-light)", marginBottom: "12px" }}>Get in Touch</span></Reveal>
          <Reveal delay={0.1}><h1 style={{ color: "var(--white)", maxWidth: "600px", margin: "0 auto 16px" }}>Let's <span style={{ color: "var(--gold)" }}>Shake Things Up</span></h1></Reveal>
          <Reveal delay={0.15}><p style={{ color: "rgba(255,255,255,0.65)", maxWidth: "520px", margin: "0 auto", fontSize: "1.05rem" }}>Have a question? Drop us a message and we'll get back to you within 24 hours.</p></Reveal>
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to top, var(--blush-tint), transparent)" }} />
      </section>

      {/* Form + Info */}
      <section className="sb-section">
        <div className="sb-container">
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
            gap: "clamp(32px, 5vw, 60px)",
          }}>
            {/* Form */}
            <Reveal>
              <div style={{
                background: "var(--white)", borderRadius: "var(--radius-lg)", padding: "clamp(28px, 4vw, 40px)",
                boxShadow: "var(--shadow-md)", border: "1px solid rgba(212,168,83,0.08)",
              }}>
                {submitted ? (
                  <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <span style={{ fontSize: "3rem", display: "block", marginBottom: "16px" }}>🎉</span>
                    <h3 style={{ color: "var(--plum)", marginBottom: "10px" }}>Thank You!</h3>
                    <p style={{ color: "var(--text-secondary)" }}>
                      We've received your message and will be in touch within 24 hours.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 style={{ color: "var(--plum)", marginBottom: "6px" }}>Send a Message</h3>
                    <p style={{ color: "var(--text-secondary)", marginBottom: "24px", fontSize: "0.92rem" }}>
                      Have a general question? We'd love to hear from you.
                    </p>
                    <form name="contact" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <input type="hidden" name="form-name" value="contact" />
                      <div style={{ display: "none" }}><input name="bot-field" /></div>

                      {/* Row 1: First Name / Last Name */}
                      <div style={ROW}>
                        <label style={SR_ONLY} htmlFor="c-firstName">First Name</label>
                        <input id="c-firstName" name="firstName" className="sb-input" type="text" placeholder="First Name *" value={form.firstName} onChange={handleChange("firstName")} required autoComplete="given-name" />
                        <label style={SR_ONLY} htmlFor="c-lastName">Last Name</label>
                        <input id="c-lastName" name="lastName" className="sb-input" type="text" placeholder="Last Name *" value={form.lastName} onChange={handleChange("lastName")} required autoComplete="family-name" />
                      </div>

                      {/* Email */}
                      <label style={SR_ONLY} htmlFor="c-email">Email Address</label>
                      <input id="c-email" name="email" className="sb-input" type="email" placeholder="Email Address *" value={form.email} onChange={handleChange("email")} required autoComplete="email" />

                      {/* Message */}
                      <label style={SR_ONLY} htmlFor="c-message">Message</label>
                      <textarea id="c-message" name="message" className="sb-input" rows={5} value={form.message} onChange={handleChange("message")} placeholder="Your message…" required />

                      {error && (
                        <p style={{ fontSize: "0.88rem", color: "#C0392B", textAlign: "center", padding: "10px 14px", background: "rgba(192,57,43,0.07)", borderRadius: "var(--radius-sm)", margin: 0 }}>
                          Something went wrong. Please try again or email us at{" "}
                          <a href="mailto:hello@sillybutterfly.com" style={{ color: "#C0392B" }}>hello@sillybutterfly.com</a>.
                        </p>
                      )}
                      <button className="sb-btn sb-btn-primary" type="submit" disabled={loading} style={{ width: "100%", justifyContent: "center", opacity: loading ? 0.7 : 1 }}>
                        {loading ? "Sending…" : <>Send Message <IconArrow /></>}
                      </button>
                      <p style={{ fontSize: "0.78rem", color: "var(--text-light)", textAlign: "center" }}>
                        We usually respond within 24 hours. No spam, ever.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </Reveal>

            {/* Contact Info */}
            <Reveal delay={0.15}>
              <div>
                <h3 style={{ color: "var(--plum)", marginBottom: "18px" }}>Other Ways to Reach Us</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginBottom: "36px" }}>
                  {[
                    { icon: <IconMail />, label: "Email", value: "hello@sillybutterfly.com", sub: "For enquiries & bookings" },
                    { icon: <IconMapPin />, label: "Service Area", value: "Ask us about your area", sub: "We come to you!" },
                    { icon: <IconInstagram />, label: "Instagram", value: "@sillybutterflycocktails", sub: "Follow us for event inspo" },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                      <div style={{
                        width: "44px", height: "44px", borderRadius: "var(--radius-sm)",
                        background: "var(--blush-glow)", display: "flex", alignItems: "center",
                        justifyContent: "center", color: "var(--gold)", flexShrink: 0,
                      }}>
                        {item.icon}
                      </div>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: "0.92rem", color: "var(--plum)", display: "block" }}>{item.label}</span>
                        <span style={{ fontSize: "0.92rem", color: "var(--text-secondary)" }}>{item.value}</span>
                        <span style={{ fontSize: "0.8rem", color: "var(--text-light)", display: "block" }}>{item.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{
                  background: "var(--blush-glow)", borderRadius: "var(--radius-md)", padding: "24px",
                  border: "1px solid rgba(212,168,83,0.1)",
                }}>
                  <h4 style={{ fontSize: "1rem", color: "var(--plum)", marginBottom: "8px" }}>Why Enquire with Silly Butterfly?</h4>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {[
                      "No obligation — just a friendly, tailored quote",
                      "We respond within 24 hours",
                      "Flexible packages to suit any budget",
                      "Personalised service from first enquiry to event day",
                    ].map((p, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.88rem", color: "var(--text-secondary)" }}>
                        <span style={{ color: "var(--gold)" }}><IconCheck /></span> {p}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
